import { CareEnvironment, FundingPolicyType } from '@shared';

type CareEnvironmentDiscountConfig = {
  rateDiscountPercentage: number;
  label: string;
  description: string;
};

type PolicyDiscountConfig = {
  [K in CareEnvironment]?: CareEnvironmentDiscountConfig;
};

type CareEnvironmentRateDiscounts = {
  [K in FundingPolicyType]?: PolicyDiscountConfig;
};

export const careEnvironmentRateDiscounts: CareEnvironmentRateDiscounts = {
  [FundingPolicyType.careScoutCareAssurance]: {
    [CareEnvironment.home]: {
      rateDiscountPercentage: 11,
      label: 'CQN Savings',
      description: `Discount on home care: 11%`,
    },
  },
};

export function getApplicableCareEnvironmentDiscount(
  multipleFundingSources: MultipleFundingSources,
  careEnvironment: CareEnvironment,
): { percent: number; label: string; description: string; policyType: number } | null {
  const policies = Object.values(multipleFundingSources.ltcPolicy || {});

  for (const policy of policies) {
    if (!policy || !policy.hasPolicyFunding || policy.isDeactivated === true) {
      continue;
    }

    const policyType = policy.policyType;
    if (!policyType) {
      continue;
    }

    const policyDiscounts = careEnvironmentRateDiscounts[policyType];
    if (!policyDiscounts) {
      continue;
    }

    const discountDef = policyDiscounts[careEnvironment];
    if (!discountDef) {
      continue;
    }

    return {
      percent: discountDef.rateDiscountPercentage,
      label: discountDef.label,
      description: discountDef.description,
      policyType: policyType,
    };
  }

  return null;
}

export function applyDiscountsToCareEnvironmentCosts(
  clientLocalCareEnvironmentCosts: LocalCareEnvironmentCosts,
  multipleFundingSources: MultipleFundingSources,
): LocalCareEnvironmentCosts {
  const result: LocalCareEnvironmentCosts = {} as LocalCareEnvironmentCosts;

  const careEnvironments: CareEnvironment[] = [
    CareEnvironment.home,
    CareEnvironment.assistedLiving,
    CareEnvironment.fullCareFacility,
    CareEnvironment.independentLiving,
  ];

  for (const careEnvironment of careEnvironments) {
    const baseData = clientLocalCareEnvironmentCosts[careEnvironment];
    if (!baseData) {
      continue;
    }

    const discount = getApplicableCareEnvironmentDiscount(multipleFundingSources, careEnvironment);
    const originalRateAmount = baseData.originalRateAmount || baseData.rateAmount;
    const discountedRateAmount = discount
      ? Math.round(originalRateAmount * (1 - discount.percent / 100))
      : originalRateAmount;

    result[careEnvironment] = {
      ...baseData,
      rateAmount: discountedRateAmount,
      originalRateAmount: originalRateAmount,
      discount: discount
        ? {
            ...discount,
            amount: originalRateAmount - discountedRateAmount,
            reason: 'policyType' as const,
            description: discount.description,
          }
        : undefined,
    };
  }

  return result;
}
